/* ==========================================================================
   A. Stockist - store locator
   MapLibre greyscale map + filterable store list + "find a store near me".

   Loads MapLibre GL (v5.10.0, last UMD line) from jsDelivr on demand.
   NOTE: v6+ ships ESM-only (no dist/maplibre-gl.js UMD), so stay on v5.
   Exposes window.Stockist (init + helpers) for the theme and tests.
   ========================================================================== */
(function (global, document) {
  'use strict';

  var MAPLIBRE_VERSION = '5.10.0';
  var MAPLIBRE_BASE_URL =
    'https://cdn.jsdelivr.net/npm/maplibre-gl@' + MAPLIBRE_VERSION + '/dist/maplibre-gl';

  // OpenFreeMap vector style (Liberty) — completely free, no API key, no
  // signup, no rate limits. The source TileJSON (tiles.openfreemap.org/planet)
  // carries the © OpenFreeMap © OpenMapTiles © OpenStreetMap attribution line,
  // which the built-in AttributionControl renders automatically — no logo
  // overlay needed. The greyscale look comes from the CSS filter on the map
  // canvas (stockist.css), so a colourful style like Liberty is fine here.
  var OPENFREEMAP_STYLE = 'https://tiles.openfreemap.org/styles/liberty';

  var DEFAULT_CENTER = [114.1694, 22.3193]; // Hong Kong
  var DEFAULT_ZOOM = 11;
  var MAX_FIT_ZOOM = 14;

  function toRad(deg) { return (deg * Math.PI) / 180; }

  // Haversine distance between two [lng, lat] points in km.
  function haversineKm(a, b) {
    var R = 6371;
    var dLat = toRad(b[1] - a[1]);
    var dLng = toRad(b[0] - a[0]);
    var lat1 = toRad(a[1]);
    var lat2 = toRad(b[1]);
    var h =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return 2 * R * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
  }

  // "2.4 km" (< 10, one decimal) / "12 km" (rounded up).
  function formatDistance(km) {
    if (!isFinite(km)) return '';
    if (km < 10) {
      var rounded = Math.round(km * 10) / 10;
      return String(rounded).replace(/\.0$/, '') + ' km';
    }
    return Math.round(km) + ' km';
  }

  function uniqueSorted(values) {
    var seen = {};
    var out = [];
    values.forEach(function (value) {
      var v = String(value == null ? '' : value).trim();
      if (v && !seen[v]) { seen[v] = true; out.push(v); }
    });
    return out.sort(function (a, b) { return a.localeCompare(b); });
  }

  var PIN_SVG =
    '<svg class="stockist-pin__svg" viewBox="0 0 24 24" aria-hidden="true">' +
    '<path d="M12 2C7.6 2 4 5.6 4 10c0 5.4 8 12 8 12s8-6.6 8-12c0-4.4-3.6-8-8-8z"/>' +
    '<circle class="stockist-pin__dot" cx="12" cy="10" r="3"/>' +
    '</svg>';

  var USER_PIN_SVG =
    '<svg class="stockist-pin__svg" viewBox="0 0 24 24" aria-hidden="true">' +
    '<circle cx="12" cy="12" r="9"/>' +
    '<circle class="stockist-pin__dot" cx="12" cy="12" r="3"/>' +
    '</svg>';

  function parseStores(root) {
    // Pages with >50 stores render extra Stockist sections with "Data only"
    // enabled — those render only a page-level data script, no root div, so
    // merge every on-page script (dedupe by id) instead of root-scoped
    // lookup. DOM order == section render order, preserving block order.
    var scripts = document.querySelectorAll('[data-stockist-data]');
    if (!scripts.length && root) {
      scripts = root.querySelectorAll('[data-stockist-data]');
    }
    var stores = [];
    var seen = {};
    scripts.forEach(function (script) {
      try {
        var raw = JSON.parse(script.textContent || '[]');
        raw.forEach(function (store) {
          if (seen[store.id]) return;
          seen[store.id] = true;
          stores.push({
            id: store.id,
            name: store.name,
            address: store.address,
            website: store.website || '',
            city: store.city,
            country: store.country,
            lat: parseFloat(store.lat),
            lng: parseFloat(store.lng),
            image: store.image || '',
            featured: !!store.featured
          });
        });
      } catch (err) {
        // Skip a malformed script instead of dropping every store.
      }
    });
    return stores;
  }

  function measureHeaderHeight(root) {
    var header = document.querySelector('.main-site-header');
    var h = header ? header.getBoundingClientRect().height : 0;
    root.style.setProperty('--stockist-header-h', (h || 100) + 'px');
  }

  function initStockist(root) {
    if (!root) return null;
    if (root.getAttribute('data-stockist-ready') === 'true') return null;
    root.setAttribute('data-stockist-ready', 'true');

    var stores = parseStores(root);
    var featuredIndexes = [];
    var normalIndexes = [];
    stores.forEach(function (store, i) {
      if (store.featured) { featuredIndexes.push(i); } else { normalIndexes.push(i); }
    });
    var baseOrder = featuredIndexes.concat(normalIndexes);
    var state = {
      map: null,
      markers: {},     // index -> { marker, el, onMap }
      userMarker: null,
      userPos: null,   // [lng, lat]
      expanded: -1,    // store index
      located: false,
      displayOrder: baseOrder.slice(),
      featuredOnly: featuredIndexes.length > 0 && featuredIndexes.length < stores.length,
      zoom: parseInt(root.getAttribute('data-stockist-zoom') || DEFAULT_ZOOM, 10) || DEFAULT_ZOOM
    };

    var mapEl = root.querySelector('[data-stockist-map]');
    var countrySel = root.querySelector('[data-stockist-country]');
    var citySel = root.querySelector('[data-stockist-city]');
    var locateBtn = root.querySelector('[data-stockist-locate]');
    var locateLabel = root.querySelector('[data-stockist-locate-label]');
    var statusEl = root.querySelector('[data-stockist-status]');
    var listEl = root.querySelector('[data-stockist-list]');
    var emptyEl = root.querySelector('[data-stockist-empty]');
    var countEl = root.querySelector('[data-stockist-count]');
    var tabsEl = root.querySelector('[data-stockist-tabs]');
    var firstRender = true;

    if (!mapEl || !listEl) return null;

    measureHeaderHeight(root);

    function showStatus(message) {
      if (!statusEl) return;
      statusEl.textContent = message;
      statusEl.hidden = !message;
    }

    function endLocate(reEnable) {
      if (!locateBtn) return;
      locateBtn.disabled = !reEnable;
      locateBtn.removeAttribute('aria-busy');
      if (locateLabel) locateLabel.textContent = locateBtn.getAttribute('data-label');
    }
    if (locateBtn) locateBtn.setAttribute('data-label', locateLabel ? locateLabel.textContent : '');

    /* ---------- filters ---------- */

    function countries() {
      return uniqueSorted(stores.map(function (s) { return s.country; }));
    }

    function citiesFor(country) {
      return uniqueSorted(
        stores
          .filter(function (s) { return !country || s.country === country; })
          .map(function (s) { return s.city; })
      );
    }

    function buildSelect(select, values, allLabel) {
      select.innerHTML = '';
      var option = document.createElement('option');
      option.value = '';
      option.textContent = allLabel;
      select.appendChild(option);
      values.forEach(function (value) {
        var opt = document.createElement('option');
        opt.value = value;
        opt.textContent = value;
        select.appendChild(opt);
      });
    }

    function populateCountryOptions() {
      buildSelect(countrySel, countries(), 'Country/Region');
    }

    function populateCityOptions() {
      buildSelect(citySel, citiesFor(countrySel.value), 'City');
    }

    function visibleIndexes() {
      var country = countrySel ? countrySel.value : '';
      var city = citySel ? citySel.value : '';
      return stores
        .map(function (s, i) { return { s: s, i: i }; })
        .filter(function (entry) {
          return (!country || entry.s.country === country) &&
                 (!city || entry.s.city === city);
        })
        .map(function (entry) { return entry.i; });
    }

    function applyFilters() {
      var visible = visibleIndexes();
      if (state.located) {
        state.displayOrder = state.displayOrder.filter(function (i) { return visible.indexOf(i) !== -1; });
      } else {
        state.displayOrder = baseOrder.filter(function (i) { return visible.indexOf(i) !== -1; });
      }
      if (firstRender) {
        firstRender = false;
      } else if (countEl) {
        var n = state.displayOrder.length;
        countEl.textContent = n === 0 ? 'No matching stores' : n + ' store' + (n === 1 ? '' : 's');
      }
      renderList();
      renderMarkers();
      fitToVisible();
      if (state.expanded >= 0 && visible.indexOf(state.expanded) === -1) setExpanded(-1, undefined);
    }

    function revealAll() {
      if (state.featuredOnly) {
        state.featuredOnly = false;
        renderMarkers();
      }
    }

    /* ---------- list ---------- */

    function storeImageEl(store) {
      if (!store.image) return null;
      var img = document.createElement('img');
      img.className = 'stockist__item-image';
      img.src = store.image;
      img.alt = store.name;
      img.setAttribute('loading', 'lazy');
      return img;
    }

    function storeWebsiteEl(store) {
      if (!store.website) return null;
      var label = store.website.replace(/^https?:\/\/(www\.)?/i, '').replace(/\/+$/, '');
      var link = document.createElement('a');
      link.className = 'stockist__item-website';
      link.href = store.website;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.textContent = label;
      return link;
    }

    function hasStreetAddress(store) {
      return !!(store.address && store.address.toLowerCase() !== (store.city || '').toLowerCase());
    }

    function storeDirectionsEl(store) {
      if (!hasStreetAddress(store)) return null;
      var link = document.createElement('a');
      link.className = 'stockist__item-directions';
      link.href = 'https://www.google.com/maps/dir/?api=1&destination=' +
                  encodeURIComponent(store.lat + ',' + store.lng);
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.textContent = 'GET DIRECTIONS';
      return link;
    }

    function renderList() {
      listEl.innerHTML = '';
      emptyEl.hidden = state.displayOrder.length > 0;

      state.displayOrder.forEach(function (index) {
        var store = stores[index];
        // Join address + city + country, skipping the city when the address
        // already contains it (e.g. "18 SALISBURY ROAD, TSIM SHA TSUI, HK").
        var address = store.address ? store.address : '';
        var city = store.city || '';
        if (city && address.toLowerCase().indexOf(city.toLowerCase()) === -1) {
          if (address) { address = address + ', ' + city; }
          else if (city !== store.country) { address = city; }
        }
        address = address ? address + ', ' + store.country : store.country;

        var item = document.createElement('li');
        item.className = 'stockist__item' + (state.located ? ' is-located' : '');
        item.dataset.index = index;

        var head = document.createElement('button');
        head.type = 'button';
        head.className = 'stockist__item-head';
        head.id = 'stockist-head-' + index;
        head.setAttribute('aria-expanded', 'false');
        head.setAttribute('aria-controls', 'stockist-body-' + index);

        var img = storeImageEl(store);
        if (img) head.appendChild(img);

        var details = document.createElement('span');
        details.className = 'stockist__item-details';

        var name = document.createElement('span');
        name.className = 'stockist__item-name';
        name.textContent = store.name;
        details.appendChild(name);

        var addr = document.createElement('span');
        addr.className = 'stockist__item-address';
        addr.textContent = address;
        details.appendChild(addr);

        if (hasStreetAddress(store)) {
          var dist = document.createElement('span');
          dist.className = 'stockist__item-dist';
          dist.textContent = state.located ? formatDistance(store.distanceKm) : '';
          details.appendChild(dist);
        }

        var icon = document.createElement('span');
        icon.className = 'stockist__item-icon';
        icon.setAttribute('aria-hidden', 'true');

        head.appendChild(details);
        head.appendChild(icon);

        var body = document.createElement('div');
        body.className = 'stockist__item-body';
        body.id = 'stockist-body-' + index;
        body.setAttribute('role', 'region');
        body.setAttribute('aria-labelledby', 'stockist-head-' + index);
        var inner = document.createElement('div');
        inner.className = 'stockist__item-body-inner';
        var content = document.createElement('div');
        content.className = 'stockist__item-body-content';
        var website = storeWebsiteEl(store);
        if (website) content.appendChild(website);
        var directions = storeDirectionsEl(store);
        if (directions) content.appendChild(directions);
        inner.appendChild(content);
        body.appendChild(inner);

        item.appendChild(head);
        item.appendChild(body);
        listEl.appendChild(item);
      });
    }

    function onListClick(event) {
      var head = event.target.closest ? event.target.closest('.stockist__item-head') : null;
      if (!head) return;
      var item = head.closest('.stockist__item');
      if (!item) return;
      var index = parseInt(item.dataset.index, 10);
      if (!isFinite(index)) return;
      revealAll();
      setExpanded(state.expanded === index ? -1 : index, index);
    }

    function setExpanded(index, fromIndex) {
      state.expanded = index;
      var items = listEl.querySelectorAll('.stockist__item');
      items.forEach(function (itemEl) {
        var i = parseInt(itemEl.dataset.index, 10);
        var isOpen = i === index;
        itemEl.classList.toggle('is-open', isOpen);
        var head = itemEl.querySelector('.stockist__item-head');
        if (head) head.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      });
      setActivePin(index);
      if (index >= 0) flyToStore(stores[index]);
    }

    function scrollToItemTop(index) {
      var item = listEl.querySelector('.stockist__item[data-index="' + index + '"]');
      if (!item) return;
      var listTop = listEl.getBoundingClientRect().top;
      var itemTop = item.getBoundingClientRect().top;
      listEl.scrollTop += itemTop - listTop;
    }

    /* ---------- map ---------- */

    function initMap() {
      if (!global.maplibregl) return;
      state.map = new global.maplibregl.Map({
        container: mapEl,
        style: OPENFREEMAP_STYLE,
        center: DEFAULT_CENTER,
        zoom: state.zoom,
        attributionControl: { customAttribution: '', compact: false }
      });
      if (state.map.addControl && global.maplibregl.NavigationControl) {
        state.map.addControl(new global.maplibregl.NavigationControl({ showCompass: false }), 'top-right');
      }
      if (state.map.on) {
        state.map.on('load', function () {
          renderMarkers();
          fitToVisible();
        });
      }
      global.addEventListener('resize', function () {
        measureHeaderHeight(root);
        if (state.map && state.map.resize) state.map.resize();
      });
    }

    function renderMarkers() {
      if (!state.map || !global.maplibregl.Marker) return;

      var visibleSet = state.featuredOnly ? featuredIndexes : state.displayOrder;
      visibleSet.forEach(function (index) {
        var store = stores[index];
        var entry = state.markers[index];
        if (!entry) {
          var el = document.createElement('div');
          el.className = 'stockist-pin';
          el.innerHTML = PIN_SVG;
          el.setAttribute('role', 'button');
          el.setAttribute('tabindex', '0');
          el.setAttribute('aria-label', store.name);
          el.addEventListener('click', function () {
            revealAll();
            setExpanded(index, index);
            scrollToItemTop(index);
          });
          el.addEventListener('keydown', function (e) {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              revealAll();
              setExpanded(index, index);
              scrollToItemTop(index);
            }
          });
          var marker = new global.maplibregl.Marker({ element: el }).setLngLat([store.lng, store.lat]);
          entry = { marker: marker, el: el, onMap: false };
          state.markers[index] = entry;
        }
        if (!entry.onMap) {
          entry.marker.addTo(state.map);
          entry.onMap = true;
        }
      });

      Object.keys(state.markers).forEach(function (key) {
        var index = parseInt(key, 10);
        var entry = state.markers[key];
        if (visibleSet.indexOf(index) === -1 && entry.onMap) {
          entry.marker.remove();
          entry.onMap = false;
        }
      });

      setActivePin(state.expanded);
    }

    function setActivePin(index) {
      Object.keys(state.markers).forEach(function (key) {
        var entry = state.markers[key];
        entry.el.classList.toggle('stockist-pin--active', parseInt(key, 10) === index);
      });
    }

    function addUserMarker() {
      if (!state.userPos || !state.map) return;
      if (state.userMarker) state.userMarker.marker.remove();
      var el = document.createElement('div');
      el.className = 'stockist-pin stockist-pin--user';
      el.innerHTML = USER_PIN_SVG;
      el.setAttribute('role', 'img');
      el.setAttribute('aria-label', 'Your location');
      state.userMarker = new global.maplibregl.Marker({ element: el })
        .setLngLat(state.userPos)
        .addTo(state.map);
    }

    function fitToVisible() {
      if (!state.map) return;
      var visibleSet = state.featuredOnly ? featuredIndexes : state.displayOrder;
      var pts = visibleSet.map(function (i) { return [stores[i].lng, stores[i].lat]; });
      if (!pts.length) return;
      var currentZoom = state.map.getZoom ? state.map.getZoom() : state.zoom;
      if (pts.length === 1) {
        state.map.flyTo({ center: pts[0], zoom: Math.max(currentZoom, 13), duration: 500 });
        return;
      }
      if (global.maplibregl.LngLatBounds) {
        var bounds = new global.maplibregl.LngLatBounds();
        pts.forEach(function (pt) { bounds.extend(pt); });
        state.map.fitBounds(bounds, { padding: { top: 48, right: 48, bottom: 48, left: 48 }, maxZoom: MAX_FIT_ZOOM, duration: 500 });
      } else {
        state.map.fitBounds(pts, { padding: { top: 48, right: 48, bottom: 48, left: 48 }, maxZoom: MAX_FIT_ZOOM, duration: 500 });
      }
    }

    function flyToStore(store) {
      if (!state.map || !store) return;
      state.map.flyTo({
        center: [store.lng, store.lat],
        zoom: Math.max(state.map.getZoom ? state.map.getZoom() : state.zoom, 14),
        duration: 900
      });
    }

    /* ---------- locate ---------- */

    function handleLocate() {
      if (state.located) return;
      locateBtn.disabled = true;
      locateBtn.setAttribute('aria-busy', 'true');
      if (locateLabel) locateLabel.textContent = 'Locating\u2026';
      var geo = global.navigator && global.navigator.geolocation;
      if (!geo) {
        showStatus('Location is not supported by this browser.');
        endLocate(true);
        return;
      }
      geo.getCurrentPosition(onLocated, onLocationError, {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 60000
      });
    }

    function onLocated(position) {
      revealAll();
      state.userPos = [
        position.coords.longitude,
        position.coords.latitude
      ];
      state.located = true;

      stores.forEach(function (store, i) {
        store.distanceKm = haversineKm(state.userPos, [store.lng, store.lat]);
      });

      var ranked = stores
        .map(function (_, i) { return i; })
        .sort(function (a, b) {
          var aHas = hasStreetAddress(stores[a]);
          var bHas = hasStreetAddress(stores[b]);
          if (aHas !== bHas) return aHas ? -1 : 1;
          return stores[a].distanceKm - stores[b].distanceKm;
        });
      var visible = visibleIndexes();
      state.displayOrder = ranked.filter(function (i) { return visible.indexOf(i) !== -1; });

      renderList();
      renderMarkers();
      addUserMarker();
      showStatus('Showing stores near you.');

      if (state.displayOrder.length) {
        setExpanded(state.displayOrder[0], state.displayOrder[0]);
      }
      endLocate(false);
    }

    function onLocationError(err) {
      var code = err && err.code;
      var message =
        code === 1 ? 'Location access was denied. You can still use the filters below.' :
        code === 2 ? 'We could not determine your location.' :
        code === 3 ? 'We could not determine your location in time. Please try again.' :
        'We could not determine your location.';
      showStatus(message);
      endLocate(true);
    }

    /* ---------- mobile list/map tabs ---------- */

    function switchTab(tab) {
      if (root.getAttribute('data-stockist-tab') === tab) return;
      root.setAttribute('data-stockist-tab', tab);
      if (tabsEl) {
        tabsEl.querySelectorAll('.stockist__tab').forEach(function (btn) {
          btn.setAttribute('aria-selected', btn.getAttribute('data-stockist-tab') === tab ? 'true' : 'false');
        });
      }
      if (tab === 'map' && state.map) {
        if (state.map.resize) state.map.resize();
        fitToVisible();
      }
    }

    /* ---------- wiring ---------- */

    populateCountryOptions();
    populateCityOptions();

    if (countrySel) countrySel.addEventListener('change', function () {
      revealAll();
      populateCityOptions();
      applyFilters();
    });
    if (citySel) citySel.addEventListener('change', function () {
      revealAll();
      applyFilters();
    });

    if (listEl) listEl.addEventListener('click', onListClick);
    if (locateBtn) locateBtn.addEventListener('click', handleLocate);
    if (tabsEl) tabsEl.addEventListener('click', function (event) {
      var btn = event.target.closest ? event.target.closest('[data-stockist-tab]') : null;
      if (!btn) return;
      switchTab(btn.getAttribute('data-stockist-tab'));
    });

    applyFilters();

    if (global.maplibregl) {
      initMap();
    } else {
      var link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = MAPLIBRE_BASE_URL + '.css';
      document.head.appendChild(link);

      var script = document.createElement('script');
      script.src = MAPLIBRE_BASE_URL + '.js';
      script.async = true;
      script.onload = function () { initMap(); };
      script.onerror = function () { showStatus('Could not load the map library.'); };
      document.head.appendChild(script);
    }

    return {
      stores: stores,
      state: state,
      getVisible: function () { return state.displayOrder.slice(); }
    };
  }

  function boot() {
    var roots = document.querySelectorAll('[data-stockist]');
    for (var i = 0; i < roots.length; i++) initStockist(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  global.Stockist = {
    init: initStockist,
    haversineKm: haversineKm,
    formatDistance: formatDistance,
    uniqueSorted: uniqueSorted,
    parseStores: parseStores
  };
})(window, document);